import React, { useState, useEffect } from "react";
import { db, storage } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import "./css/ManageUsers.css";
import "./css/EditUsers.css";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const EditUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const [pendingDeleteImages, setPendingDeleteImages] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: ''
  });
  const [expiresAtOption, setExpiresAtOption] = useState("");
  const [customExpiryDate, setCustomExpiryDate] = useState("");

  const handleImageClick = (url) => {
    setSelectedImage(url);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const openEmailModal = () => {
    setShowEmailModal(true);
    setEmailData({
      subject: `Message from Admin - ${userDetails.name || 'User'}`,
      message: ''
    });
    document.body.style.overflow = 'hidden';
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailData({ subject: '', message: '' });
    document.body.style.overflow = 'auto';
  };

  const handleSendEmail = () => {
    if (!emailData.message.trim()) {
      alert('Please enter a message');
      return;
    }

    const mailtoLink = `mailto:${userDetails.email}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.message)}`;
    window.location.href = mailtoLink;
    
    setTimeout(() => {
      closeEmailModal();
    }, 500);
  };

  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    location: "",
    dateOfBirth: "",
    phoneNumber: "",
    userGender: "",
    genderPreference: "",
    subscriptions: {
      availedBoost: 0,
      boostedUntil:null,
      planName: "Free",
      validUntil: ""
    },
    lookingFor: "",
    height: "",
    status: "",
    onlineStatus: false,
    notificationStatus: false,
    aboutMe: "",
    currentDiscountPercentage: 0,
    motherTongue: "",
    religion: "",
    community: "",
    degree: "",
    dayJob: "",
    growUpCountry: "",
    originCountry: "",
    settledCountry: "",
    bodyBuild: "",
    profileImageUrls: [],
    fcmToken: "",
    profileDiscovery: false,
    isDisabled: false,
    expiresAt: "",
    currentPosition: {
      address: "",
      city: "",
      latitude: "",
      longitude: ""
    },
    password: "",
    confirmPassword: "",
    selectedLikesInvolvesMap: {
      books: [],
      childrenView: [],
      foods: [],
      hobbies: [],
      interests: [],
      movies: [],
      music: [],
      relaxWay: [],
      sleepingHabit: [],
      sports: [],
      tvShows: [],
      vacations: [],
    },
    selectedPersonalityTraitsMap: {
      drink: "",
      eveningRoutine: [],
      exercise: "",
      passions: [],
      personalityType: "",
      pets: [],
      poison: [],
      smoke: "",
      starSign: "",
      tripsType: [],
      weatherType: [],
      weekendActivities: [],
      weekendNight: []
    },
    uploadedImageUrls: [],
  });
 
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const fetchedData = userSnap.data();
  
          const formattedDate = fetchedData.dateOfBirth
            ? formatDateForInput(fetchedData.dateOfBirth)
            : "";
  
          console.log("Fetched Mother Tongue:", fetchedData.motherTongue);
  
          setUserDetails({
            ...fetchedData,
            dateOfBirth: formattedDate,
            motherTongue: (fetchedData.motherTongue || "").trim(),
            isDisabled: fetchedData.isDisabled ?? false,
            expiresAt: fetchedData.expiresAt || "",
          });

          // Set expiry option based on existing expiresAt
          if (fetchedData.expiresAt) {
            setExpiresAtOption("custom");
            setCustomExpiryDate(formatDateForInput(fetchedData.expiresAt));
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
  
    fetchUserData();
  }, [userId]);
  
const handleFileChange = async (e) => {
  const files = e.target.files;
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

  const existingImagesCount = userDetails.profileImageUrls.length;
  const totalImagesCount = existingImagesCount + files.length;

  if (existingImagesCount >= 3 && totalImagesCount > 6) {
    alert("You can only upload a maximum of 6 images");
    return;
  }

  const fileUrls = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (!allowedTypes.includes(file.type)) {
      alert(`File "${file.name}" is not allowed. Only JPG, JPEG, and PNG files are accepted.`);
      continue;
    }

    const storageRef = ref(storage, `profile_images/${file.name}`);
    try {
      const uploadSnapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadSnapshot.ref);
      fileUrls.push(downloadURL);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  }

  if (fileUrls.length > 0) {
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      profileImageUrls: [...prevDetails.profileImageUrls, ...fileUrls],
    }));
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split('.');
  
    if (name === "currentDiscountPercentage") {
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        [name]: value === "" ? 0 : Number(value),
      }));
    }else if (name === "isDisabled") {
      const isDisabledValue = value === "true";
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        isDisabled: isDisabledValue,
        profileDiscovery: !isDisabledValue,
      }));
      
      // Reset expiry fields when enabling account
      if (!isDisabledValue) {
        setExpiresAtOption("");
        setCustomExpiryDate("");
        setUserDetails((prevDetails) => ({
          ...prevDetails,
          expiresAt: "",
        }));
      }
    } else if (nameParts.length > 1) {
      setUserDetails((prevDetails) => {
        const updatedDetails = JSON.parse(JSON.stringify(prevDetails)); 
  
        if (nameParts[0] === 'subscriptions') {
          if (!updatedDetails.subscriptions) {
            updatedDetails.subscriptions = {
              availedBoost: 0,
              boostedUntil: null,
              planName: "Free",
              validUntil: ""
            };
          }
  
          if (name === "subscriptions.planName") {
            updatedDetails.subscriptions.planName = value;
  
            if (value === "Free") {
              updatedDetails.subscriptions.validUntil = "";
              updatedDetails.subscriptions.availedBoost = 0;
              updatedDetails.subscriptions.boostedUntil = null;
            }
          } else {
            updatedDetails.subscriptions[nameParts[1]] = value;
          }
        } else {
          let currentObject = updatedDetails;
          for (let i = 0; i < nameParts.length - 1; i++) {
            if (!currentObject[nameParts[i]]) {
              currentObject[nameParts[i]] = {};
            }
            currentObject = currentObject[nameParts[i]];
          }
          currentObject[nameParts[nameParts.length - 1]] = value;
        }
  
        return updatedDetails;
      });
    } else {
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        [name]: value === "true" ? true : value === "false" ? false : value,
      }));
    }
  };

  const calculateExpiryDate = (option) => {
    const today = new Date();
    let expiryDate = new Date(today);

    switch(option) {
      case "1week":
        expiryDate.setDate(today.getDate() + 7);
        break;
      case "1month":
        expiryDate.setMonth(today.getMonth() + 1);
        break;
      case "3months":
        expiryDate.setMonth(today.getMonth() + 3);
        break;
      case "6months":
        expiryDate.setMonth(today.getMonth() + 6);
        break;
      default:
        return "";
    }

    return formatDateYYYYMMDD(expiryDate); // 🔥 YYYY-MM-DD
  };

  const handleExpiresAtChange = (e) => {
    const option = e.target.value;
    setExpiresAtOption(option);

    if (option === "custom") {
      setCustomExpiryDate("");
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        expiresAt: "",
      }));
    } else if (option) {
      const calculatedDate = calculateExpiryDate(option);
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        expiresAt: calculatedDate,
      }));
      setCustomExpiryDate("");
    } else {
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        expiresAt: "",
      }));
      setCustomExpiryDate("");
    }
  };

  const handleCustomDateChange = (e) => {
    const selectedDate = e.target.value; // already YYYY-MM-DD
    setCustomExpiryDate(selectedDate);
  
    setUserDetails((prev) => ({
      ...prev,
      expiresAt: selectedDate, // 🔥 DB-la 2025-11-03
    }));
  };
  
  
  const formatDateForStorage = (date) => {
    const dateObj = new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(dateObj);
  };
  
  const formatDateForInput = (date) => {
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (userDetails.isDisabled && !userDetails.expiresAt) {
      alert("Please select an expiry date for the disabled account");
      return;
    }
  
    const userRef = doc(db, "users", userId);
  
    try {
      await setDoc(userRef, { ...userDetails }, { merge: true });
  
      for (const url of pendingDeleteImages) {
        const imageName = url.split('/').pop().split('?')[0];
        const storageRef = ref(storage, `profile_images/${imageName}`);
        try {
          await deleteObject(storageRef);
          console.log("Deleted image from storage:", imageName);
        } catch (error) {
          console.warn("Error deleting image from storage:", imageName, error);
        }
      }
  
      setPendingDeleteImages([]);
      alert("User updated successfully!");
      navigate("/manage-users");
    } catch (error) {
      console.error("Error updating user:", error);
      alert("An error occurred while updating the user!");
    }
    
  };
  

  const renderImagePreviews = () => {
    return (
      <>
        <div className="image-preview-container">
          {userDetails.profileImageUrls.map((url, index) => (
            <div key={index} className="image-preview1">
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                width="100"
                height="100"
                style={{ objectFit: "cover", borderRadius: "8px", cursor: "pointer" }}
                onClick={() => handleImageClick(url)}
              />
              <button
                type="button"
                className="delete-image-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleImageDelete(index);
                }}
                title="Delete image"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {selectedImage && (
          <div className="image-modal" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <span className="close-icon" onClick={closeModal}>&times;</span>
              <img src={selectedImage} alt="Full size" />
            </div>
          </div>
        )}
      </>
    );
  };

  const generateHeightOptions = () => {
    const options = [];
    for (let feet = 4; feet <= 7; feet++) {
      for (let inches = 0; inches < 10; inches++) {
        if (feet === 7 && inches > 0) break;
        options.push(
          <option key={`${feet}'${inches}`} value={`${feet}' ${inches}''`}>
            {`${feet}' ${inches}''`}
          </option>
        );
      }
    }
    return options;
  };

  const motherTongueList = [
    "العربية", "বাংলা", "中文", "Dansk", "Deutsch", "English", "Español", "Français", "Gaeilge", "Italiano",
    "日本語", "कश्मीरी/ كشميري", "ಕನ್ನಡ", "कोंकणी", "মৈতৈলোন্/ মণিপুরী", "मराठी", "Magyar", "മലയാളം", "नेपाली",
    "Nederlands", "ଓଡିଆ", "ਪੰਜਾਬੀ", "Português", "Русский", "سنڌي", "Svenska", "தமிழ்", "తెలుగు", "ತುಳು",
    "اردو", "ગુજરાતી", "ਪੰਜਾਬੀ"
  ];

  const handleImageDelete = (index) => {
    const imageUrlToDelete = userDetails.profileImageUrls[index];
    const updatedImageUrls = userDetails.profileImageUrls.filter((_, i) => i !== index);
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      profileImageUrls: updatedImageUrls,
    }));
    setPendingDeleteImages((prev) => [...prev, imageUrlToDelete]);
  };
  const formatDateYYYYMMDD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  
  return (
    <div className="edit-user">
      <div className="edit-user-header">
        <h2>Edit User</h2>
        <button 
          type="button"
          onClick={openEmailModal} 
          className="send-email-btn-edit"
        >
          <i className="fas fa-envelope"></i> Send Email
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            name="name"
            value={userDetails.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={userDetails.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            name="location"
            value={userDetails.location}
            onChange={handleChange}
            readOnly
            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
          />
        </div>

        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            name="dateOfBirth"
            value={userDetails.dateOfBirth}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            name="phoneNumber"
            value={userDetails.phoneNumber}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>User Gender</label>
          <select name="userGender" value={userDetails.userGender} onChange={handleChange}>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Gender Preference</label>
          <select name="genderPreference" value={userDetails.genderPreference} onChange={handleChange}>
            <option value="">Select Gender</option>
            <option value="Men">Men</option>
            <option value="Women">Women</option>
            <option value="Everyone">Everyone</option>
          </select>
        </div>

        <div className="form-group">
          <label>Plan Name</label>
          <select
            name="subscriptions.planName"
            value={userDetails.subscriptions?.planName || ""}
            onChange={handleChange}
          >
            <option value="Free">Free</option>
            <option value="Ybe Plus">Ybe Plus</option>
            <option value="Ybe Premium">Ybe Premium</option>
            <option value="Ybe Gold">Ybe Gold</option>
          </select>
        </div>

        {userDetails.subscriptions?.planName && userDetails.subscriptions?.planName !== "Free" && (
          <div className="form-group">
            <label>Valid Until</label>
            <input
              type="date"
              name="subscriptions.validUntil"
              value={userDetails.subscriptions?.validUntil?.slice(0, 10) || ""}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="form-group">
          <label>Looking For</label>
          <select name="lookingFor" value={userDetails.lookingFor} onChange={handleChange}>
            <option value="dating">Dating</option>
            <option value="marriage">Marriage</option>
            <option value="dating & marriage">Dating & Marriage</option>
          </select>
        </div>

        <div className="form-group">
          <label>Height</label>
          <select name="height" value={userDetails.height} onChange={handleChange}>
            {generateHeightOptions()}
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select name="status" value={userDetails.status} onChange={handleChange}>
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="widowed">Widowed</option>
            <option value="divorced">Divorced</option>
            <option value="separated">Separated</option>
            <option value="registered partnership">Registered Partnership</option>
          </select>
        </div>

        <div className="form-group">
          <label>About Me</label>
          <input
            type="text"
            name="aboutMe"
            value={userDetails.aboutMe}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Mother Tongue</label>
          <select name="motherTongue" value={userDetails.motherTongue} onChange={handleChange}>
            <option value="">Select Mother Tongue</option>
            {motherTongueList.map((lang, index) => (
              <option key={index} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Religion</label>
          <select name="religion" value={userDetails.religion} onChange={handleChange}>
            <option value="">Select Religion</option>
            <option value="Hindu">Hindu</option>
            <option value="Muslim">Muslim</option>
            <option value="Christian">Christian</option>
            <option value="Sikh">Sikh</option>
            <option value="Jain">Jain</option>
            <option value="Parsi">Parsi</option>
            <option value="Jew">Jew</option>
            <option value="Buddhist">Buddhist</option>
            <option value="Not religious">Not religious</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Community</label>
          <select name="community" value={userDetails.community} onChange={handleChange}>
            <option value="">Select Community</option>
            <option value="Malayali">Malayali</option>
            <option value="Punjabi">Punjabi</option>
            <option value="Gujarati">Gujarati</option>
            <option value="Telugu">Telugu</option>
            <option value="Sunni">Sunni</option>
            <option value="Shia">Shia</option>
            <option value="Tamil">Tamil</option>
            <option value="Bengali">Bengali</option>
            <option value="Marathi">Marathi</option>
            <option value="Urdu">Urdu</option>
            <option value="Kannada">Kannada</option>
            <option value="Marwari">Marwari</option>
            <option value="Sindhi">Sindhi</option>
            <option value="Kashmiri">Kashmiri</option>
            <option value="Rajasthani">Rajasthani</option>
            <option value="Jatt">Jatt</option>
          </select>
        </div>

        <div className="form-group">
          <label>Degree</label>
          <select name="degree" value={userDetails.degree} onChange={handleChange}>
            <option value="">Select Degree</option>
            <option value="Doctorate">Doctorate</option>
            <option value="PhD">PhD</option>
            <option value="Masters">Masters</option>
            <option value="Bachelors">Bachelors</option>
            <option value="Associates">Associates</option>
            <option value="Trade School">Trade School</option>
            <option value="High School">High School</option>
          </select>
        </div>

        <div className="form-group">
          <label>Day Job</label>
          <select name="dayJob" value={userDetails.dayJob} onChange={handleChange}>
            <option value="">Select Day Job</option>
            <option value="Doctor">Doctor</option>
            <option value="Engineer">Engineer</option>
            <option value="Teacher">Teacher</option>
            <option value="Actor">Actor</option>
            <option value="Accountant">Accountant</option>
            <option value="Archaeologist">Archaeologist</option>
            <option value="Architect">Architect</option>
            <option value="Artist">Artist</option>
            <option value="Aviation Professional">Aviation Professional</option>
            <option value="Beautician">Beautician</option>
            <option value="Chef">Chef</option>
            <option value="Nurse">Nurse</option>
            <option value="IT Manager">IT Manager</option>
            <option value="Bank Job">Bank Job</option>
            <option value="Marketing Manager">Marketing Manager</option>
            <option value="Fashion Designer">Fashion Designer</option>
            <option value="Business owner">Business owner</option>
            <option value="Advocate">Advocate</option>
            <option value="Biomedical Engineer">Biomedical Engineer</option>
            <option value="Biologist">Biologist</option>
            <option value="Professor">Professor</option>
          </select>
        </div>

        <div className="form-group">
          <label>Grow Up Country</label>
          <select name="growUpCountry" value={userDetails.growUpCountry} onChange={handleChange}>
            <option value="">Select Grow Up Country</option>
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
            <option value="Australia">Australia</option>
            <option value="India">India</option>
            <option value="France">France</option>
            <option value="Bulgaria">Bulgaria</option>
            <option value="Prefer Not to say">Prefer Not to say</option>
          </select>
        </div>

        <div className="form-group">
          <label>Origin Country</label>
          <select name="originCountry" value={userDetails.originCountry} onChange={handleChange}>
            <option value="">Select Origin Country</option>
            <option value="Abu Dhabi">Abu Dhabi</option>
            <option value="Ahmedabad">Ahmedabad</option>
            <option value="Auckland">Auckland</option>
            <option value="Bangkok">Bangkok</option>
            <option value="Barcelona">Barcelona</option>
            <option value="Berlin">Berlin</option>
            <option value="Bengaluru">Bengaluru</option>
            <option value="Cape Town">Cape Town</option>
            <option value="Calicut">Calicut</option>
            <option value="Chennai">Chennai</option>
            <option value="Chicago">Chicago</option>
            <option value="Cochin">Cochin</option>
            <option value="Delhi">Delhi</option>
            <option value="Doha">Doha</option>
            <option value="Dubai">Dubai</option>
            <option value="Frankfurt">Frankfurt</option>
            <option value="Hong Kong">Hong Kong</option>
            <option value="Houston">Houston</option>
            <option value="Jedah">Jedah</option>
            <option value="Jaipur">Jaipur</option>
            <option value="Johannesburg">Johannesburg</option>
            <option value="India">India</option>
            <option value="Kuala Lumpur">Kuala Lumpur</option>
            <option value="Kuwait City">Kuwait City</option>
            <option value="Lisbon">Lisbon</option>
            <option value="London">London</option>
            <option value="Los Angeles">Los Angeles</option>
            <option value="Lucknow">Lucknow</option>
            <option value="Madrid">Madrid</option>
            <option value="Melbourne">Melbourne</option>
            <option value="Milan">Milan</option>
            <option value="Montreal">Montreal</option>
            <option value="Munich">Munich</option>
            <option value="New York">New York</option>
            <option value="Ottawa">Ottawa</option>
            <option value="Paris">Paris</option>
            <option value="Pune">Pune</option>
            <option value="Riyadh">Riyadh</option>
            <option value="Rome">Rome</option>
            <option value="San Francisco">San Francisco</option>
            <option value="Seoul">Seoul</option>
            <option value="Shanghai">Shanghai</option>
            <option value="Singapore">Singapore</option>
            <option value="Sydney">Sydney</option>
            <option value="Tokyo">Tokyo</option>
            <option value="Toronto">Toronto</option>
            <option value="Trivandrum">Trivandrum</option>
            <option value="Vancouver">Vancouver</option>
            <option value="Zurich">Zurich</option>
          </select>
        </div>

        <div className="form-group">
          <label>Settled Country</label>
          <select name="settledCountry" value={userDetails.settledCountry} onChange={handleChange}>
            <option value="">Select Settled Country</option>
            <option value="Abu Dhabi">Abu Dhabi</option>
            <option value="Ahmedabad">Ahmedabad</option>
            <option value="Auckland">Auckland</option>
            <option value="Bangkok">Bangkok</option>
            <option value="Barcelona">Barcelona</option>
            <option value="Berlin">Berlin</option>
            <option value="Bengaluru">Bengaluru</option>
            <option value="Cape Town">Cape Town</option>
            <option value="Calicut">Calicut</option>
            <option value="Chennai">Chennai</option>
            <option value="Chicago">Chicago</option>
            <option value="Cochin">Cochin</option>
            <option value="Delhi">Delhi</option>
            <option value="Doha">Doha</option>
            <option value="Dubai">Dubai</option>
            <option value="Frankfurt">Frankfurt</option>
            <option value="Hong Kong">Hong Kong</option>
            <option value="Houston">Houston</option>
            <option value="Jedah">Jedah</option>
            <option value="Jaipur">Jaipur</option>
            <option value="Johannesburg">Johannesburg</option>
            <option value="Kuala Lumpur">Kuala Lumpur</option>
            <option value="Kuwait City">Kuwait City</option>
            <option value="Lisbon">Lisbon</option>
            <option value="London">London</option>
            <option value="Los Angeles">Los Angeles</option>
            <option value="Lucknow">Lucknow</option>
            <option value="Madrid">Madrid</option>
            <option value="Melbourne">Melbourne</option>
            <option value="Milan">Milan</option>
            <option value="Montreal">Montreal</option>
            <option value="Munich">Munich</option>
            <option value="New York">New York</option>
            <option value="Ottawa">Ottawa</option>
            <option value="Paris">Paris</option>
            <option value="Pune">Pune</option>
            <option value="Riyadh">Riyadh</option>
            <option value="Rome">Rome</option>
            <option value="San Francisco">San Francisco</option>

        
            <option value="Seoul">Seoul</option>
            <option value="Shanghai">Shanghai</option>
            <option value="Singapore">Singapore</option>
            <option value="Sydney">Sydney</option>
            <option value="Tokyo">Tokyo</option>
            <option value="Toronto">Toronto</option>
            <option value="Trivandrum">Trivandrum</option>
            <option value="Vancouver">Vancouver</option>
            <option value="Zurich">Zurich</option>
          </select>
        </div>

        <div className="form-group">
          <label>Body Build</label>
          <select name="bodyBuild" value={userDetails.bodyBuild} onChange={handleChange}>
            <option value="">Select Body Build</option>
            <option value="Slim/Lean">Slim/Lean</option>
            <option value="Athletic/Toned">Athletic/Toned</option>
            <option value="Average/Medium Build">Average/Medium Build</option>
            <option value="Broad Shoulders">Broad Shoulders</option>
            <option value="Muscular">Muscular</option>
            <option value="Stocky/Heavily Built">Stocky/Heavily Built</option>
            <option value="Full-Figured/Plus-size">Full-Figured/Plus-size</option>
            <option value="Petite/Small Frame">Petite/Small Frame</option>
            <option value="Tall and Lean">Tall and Lean</option>
            <option value="Prefer Not to say">Prefer Not to say</option>
          </select>
        </div>
        <div className="form-group">
      <label>Body Build</label>
      <select name="bodyBuild" value={userDetails.bodyBuild} onChange={handleChange}>
        <option value="">Select Body Build</option>
        <option value="Slim/Lean">Slim/Lean</option>
        <option value="Athletic/Toned">Athletic/Toned</option>
        <option value="Average/Medium Build">Average/Medium Build</option>
        <option value="Broad Shoulders">Broad Shoulders</option>
        <option value="Muscular">Muscular</option>
        <option value="Stocky/Heavily Built">Stocky/Heavily Built</option>
        <option value="Full-Figured/Plus-size">Full-Figured/Plus-size</option>
        <option value="Petite/Small Frame">Petite/Small Frame</option>
        <option value="Tall and Lean">Tall and Lean</option>
        <option value="Prefer Not to say">Prefer Not to say</option>
      </select>
    </div>

    <div className="form-group">
      <label>Account Status</label>
      <select name="isDisabled" value={userDetails.isDisabled} onChange={handleChange}>
        <option value={false}>Enabled</option>
        <option value={true}>Disabled</option>
      </select>
    </div>

    {userDetails.isDisabled && (
      <>
        <div className="form-group">
          <label>Expires At</label>
          <select value={expiresAtOption} onChange={handleExpiresAtChange}>
            <option value="">Select Duration</option>
            <option value="1week">1 Week</option>
            <option value="1month">1 Month</option>
            <option value="3months">3 Months</option>
            <option value="6months">6 Months</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {expiresAtOption === "custom" && (
          <div className="form-group">
            <label>Select Custom End Date</label>
            <input
              type="date"
              value={customExpiryDate}
              onChange={handleCustomDateChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        )}

        {userDetails.expiresAt && (
          <div className="form-group">
            <label>Expiry Date</label>
            <input
              type="text"
              value={userDetails.expiresAt}

              readOnly
              style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
            />
          </div>
        )}
      </>
    )}

    <div className="form-group">
      <label>Current Discount Percentage</label>
      <input
        type="text"
        name="currentDiscountPercentage"
        value={userDetails.currentDiscountPercentage}
        onChange={handleChange}
      />
    </div>

    <div className="image">
      <label>Upload Profile Image(s)</label>
      <input
        type="file"
        name="uploadedImageUrls"
        multiple
        onChange={handleFileChange}
      />
    </div>

    {renderImagePreviews()}

    <button type="submit">Save Changes</button>
  </form>

  {/* Email Modal */}
  {showEmailModal && (
    <div className="email-modal-overlay" onClick={closeEmailModal}>
      <div className="email-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h3>
            <i className="fas fa-envelope"></i> Send Email to {userDetails.name}
          </h3>
          <button className="email-modal-close" onClick={closeEmailModal}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="email-modal-body">
          <div className="email-field">
            <label>
              <i className="fas fa-user"></i> To:
            </label>
            <input 
              type="text" 
              value={userDetails.email} 
              disabled 
              className="email-input-disabled"
            />
          </div>

          <div className="email-field">
            <label>
              <i className="fas fa-heading"></i> Subject:
            </label>
            <input 
              type="text" 
              value={emailData.subject}
              onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              placeholder="Enter email subject"
              className="email-input"
            />
          </div>

          <div className="email-field">
            <label>
              <i className="fas fa-comment"></i> Message:
            </label>
            <textarea 
              value={emailData.message}
              onChange={(e) => setEmailData({...emailData, message: e.target.value})}
              placeholder="Enter your message here..."
              className="email-textarea"
              rows="8"
            />
          </div>
        </div>

        <div className="email-modal-footer">
          <button className="email-cancel-btn" onClick={closeEmailModal}>
            <i className="fas fa-times"></i> Cancel
          </button>
          <button className="email-send-btn" onClick={handleSendEmail}>
            <i className="fas fa-paper-plane"></i> Send Email
          </button>
        </div>
      </div>
    </div>
  )}
</div>

);
};
export default EditUser;